import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import {
  Calculator,
  Info,
  BookOpen,
  Lightbulb,
  Copy,
  Check,
  RotateCcw,
  TrendingUp,
  Eye,
  EyeOff,
  Download,
  Share2,
  Sparkles,
  Zap,
  CheckCircle2,
  XCircle,
  Target,
  ArrowRight,
  Hash,
  Infinity as InfinityIcon
} from 'lucide-react';
import "@/App.css";

// Limit calculation with detailed steps
const calculateLimit = (expression, variable, approachValue, direction = 'both') => {
  const steps = [];
  let current = expression.trim();
  
  steps.push(`Computing limit: lim (${variable}→${approachValue}${direction === 'left' ? '⁻' : direction === 'right' ? '⁺' : ''}) ${current}`);
  
  // Parse polynomial for simple cases
  const parsePolynomial = (expr) => {
    const termRegex = /([+-]?\d*\.?\d*)\*?([a-z])\^?(\d+)?/gi;
    const constantRegex = /^([+-]?\d+\.?\d*)$/;
    
    const terms = [];
    let match;
    expr = expr.replace(/\s/g, '');
    
    while ((match = termRegex.exec(expr)) !== null) {
      const coef = match[1] === '' || match[1] === '+' ? 1 : match[1] === '-' ? -1 : parseFloat(match[1]);
      const variable = match[2];
      const power = match[3] ? parseInt(match[3]) : 1;
      terms.push({ coef, variable, power });
    }
    
    const constMatch = expr.match(constantRegex);
    if (constMatch && terms.length === 0) {
      terms.push({ coef: parseFloat(constMatch[1]), variable: null, power: 0 });
    }
    
    return terms;
  };
  
  // Check if approaching infinity
  const isInfinity = approachValue === 'inf' || approachValue === '-inf' || approachValue === '∞' || approachValue === '-∞';
  const targetValue = isInfinity ? (approachValue === '-inf' || approachValue === '-∞' ? -Infinity : Infinity) : parseFloat(approachValue);
  
  if (isInfinity) {
    steps.push(`\nApproaching ${targetValue > 0 ? 'positive' : 'negative'} infinity...`);
  } else {
    steps.push(`\nDirect substitution: ${variable} = ${targetValue}`);
  }
  
  const terms = parsePolynomial(current);
  let result = 0;
  let isIndeterminate = false;
  let isDefined = true;
  
  // Evaluate each term
  terms.forEach((term, idx) => {
    if (term.variable === null || term.power === 0) {
      result += term.coef;
      steps.push(`  • Constant term: ${term.coef}`);
    } else {
      if (!isInfinity) {
        const value = term.coef * Math.pow(targetValue, term.power);
        if (isNaN(value) || !isFinite(value)) {
          isIndeterminate = true;
          steps.push(`  • ${term.coef}(${targetValue})^${term.power} = undefined (indeterminate form)`);
        } else {
          result += value;
          steps.push(`  • ${term.coef}(${targetValue})^${term.power} = ${value.toFixed(6)}`);
        }
      } else {
        // Behavior at infinity
        if (term.power > 0) {
          if (targetValue > 0) {
            steps.push(`  • ${term.coef}${term.variable}^${term.power} → ${term.coef > 0 ? '+' : '-'}∞ as ${variable}→∞`);
          } else {
            const sign = term.power % 2 === 0 ? (term.coef > 0 ? '+' : '-') : (term.coef > 0 ? '-' : '+');
            steps.push(`  • ${term.coef}${term.variable}^${term.power} → ${sign}∞ as ${variable}→-∞`);
          }
        }
      }
    }
  });
  
  let limitResult;
  let limitType = 'finite';
  
  if (isInfinity) {
    // Determine leading term for polynomial
    let maxPower = -Infinity;
    let leadingCoef = 0;
    terms.forEach(term => {
      if (term.variable && term.power > maxPower) {
        maxPower = term.power;
        leadingCoef = term.coef;
      }
    });
    
    if (maxPower > 0) {
      if (targetValue > 0) {
        limitResult = leadingCoef > 0 ? Infinity : -Infinity;
        limitType = 'infinite';
        steps.push(`\nDominant term: ${leadingCoef}${variable}^${maxPower}`);
        steps.push(`As ${variable}→∞, the limit is ${limitResult > 0 ? '+' : '-'}∞`);
      } else {
        const sign = maxPower % 2 === 0 ? leadingCoef : -leadingCoef;
        limitResult = sign > 0 ? Infinity : -Infinity;
        limitType = 'infinite';
        steps.push(`\nDominant term: ${leadingCoef}${variable}^${maxPower}`);
        steps.push(`As ${variable}→-∞, the limit is ${limitResult > 0 ? '+' : '-'}∞`);
      }
    } else {
      // Only constant terms
      limitResult = result;
      limitType = 'finite';
      steps.push(`\nAll terms are constant, limit = ${result}`);
    }
  } else if (isIndeterminate) {
    limitResult = "Indeterminate (0/0 or similar form)";
    limitType = 'indeterminate';
    steps.push(`\n⚠️ Indeterminate form detected - may require L'Hôpital's rule or algebraic simplification`);
  } else {
    limitResult = result;
    limitType = 'finite';
    steps.push(`\nFinal result: ${result.toFixed(6)}`);
  }
  
  return {
    expression: current,
    variable,
    approachValue: targetValue,
    direction,
    result: limitResult,
    limitType,
    steps,
    isIndeterminate
  };
};

// Limit laws and properties
const limitLaws = [
  {
    name: "Sum Law",
    formula: "lim[f(x) + g(x)] = lim f(x) + lim g(x)",
    description: "The limit of a sum is the sum of the limits",
    condition: "Both limits must exist"
  },
  {
    name: "Difference Law",
    formula: "lim[f(x) - g(x)] = lim f(x) - lim g(x)",
    description: "The limit of a difference is the difference of the limits",
    condition: "Both limits must exist"
  },
  {
    name: "Product Law",
    formula: "lim[f(x) · g(x)] = lim f(x) · lim g(x)",
    description: "The limit of a product is the product of the limits",
    condition: "Both limits must exist"
  },
  {
    name: "Quotient Law",
    formula: "lim[f(x)/g(x)] = lim f(x) / lim g(x)",
    description: "The limit of a quotient is the quotient of the limits",
    condition: "Both limits exist and lim g(x) ≠ 0"
  },
  {
    name: "Constant Multiple Law",
    formula: "lim[c · f(x)] = c · lim f(x)",
    description: "Constants can be factored out of limits",
    condition: "Limit of f(x) must exist"
  },
  {
    name: "Power Law",
    formula: "lim[f(x)]^n = [lim f(x)]^n",
    description: "The limit of a power is the power of the limit",
    condition: "Limit of f(x) must exist"
  },
  {
    name: "Root Law",
    formula: "lim ⁿ√[f(x)] = ⁿ√[lim f(x)]",
    description: "The limit of a root is the root of the limit",
    condition: "For even roots, lim f(x) must be non-negative"
  },
  {
    name: "Composition Law",
    formula: "lim f(g(x)) = f(lim g(x))",
    description: "Limits pass through continuous functions",
    condition: "f must be continuous at lim g(x)"
  },
  {
    name: "Squeeze Theorem",
    formula: "If f(x) ≤ g(x) ≤ h(x) and lim f(x) = lim h(x) = L, then lim g(x) = L",
    description: "A function squeezed between two functions with the same limit has that limit",
    condition: "Inequalities must hold near the limit point"
  },
  {
    name: "L'Hôpital's Rule",
    formula: "If lim f(x)/g(x) is 0/0 or ∞/∞, then lim f(x)/g(x) = lim f'(x)/g'(x)",
    description: "For indeterminate forms, take derivatives of numerator and denominator",
    condition: "Original limit must be indeterminate form"
  }
];

// Example problems
const exampleProblems = [
  { expr: "x^2 + 3x + 2", value: "2", desc: "Simple polynomial" },
  { expr: "x^3 - 1", value: "1", desc: "Cubic at x=1" },
  { expr: "2x^2 + x", value: "0", desc: "At origin" },
  { expr: "x^2", value: "inf", desc: "Limit at infinity" },
  { expr: "3x + 5", value: "-inf", desc: "Limit at -∞" },
  { expr: "x^3 + 2x", value: "inf", desc: "Cubic at infinity" },
];

// Copy Button Component
const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text?.toString() || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleCopy} title={`Copy ${label}`}>
      <AnimatePresence mode="wait">
        {copied ? (
          <motion.div key="check" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
            <Check className="w-3.5 h-3.5 text-green-500" />
          </motion.div>
        ) : (
          <motion.div key="copy" initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
            <Copy className="w-3.5 h-3.5" />
          </motion.div>
        )}
      </AnimatePresence>
    </Button>
  );
};

function LimitCalculator() {
  const [expression, setExpression] = useState("x^2 + 3x + 2");
  const [variable, setVariable] = useState("x");
  const [approachValue, setApproachValue] = useState("2");
  const [direction, setDirection] = useState("both");
  const [showSteps, setShowSteps] = useState(true);
  const [activeTab, setActiveTab] = useState("calculator");
  
  const result = useMemo(() => {
    try {
      return calculateLimit(expression, variable, approachValue, direction);
    } catch (e) {
      return null;
    }
  }, [expression, variable, approachValue, direction]);
  
  const handleReset = useCallback(() => {
    setExpression("x^2 + 3x + 2");
    setVariable("x");
    setApproachValue("2");
    setDirection("both");
  }, []);
  
  const loadExample = (expr, value) => {
    setExpression(expr);
    setApproachValue(value);
  };
  
  const handleExport = () => {
    if (!result) return;
    const data = {
      expression: result.expression,
      variable: result.variable,
      approachValue: result.approachValue,
      direction: result.direction,
      result: result.result,
      limitType: result.limitType,
      steps: result.steps,
      timestamp: new Date().toISOString()
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `limit-${Date.now()}.json`;
    a.click();
  };
  
  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <Helmet>
        <title>Limit Calculator - Compute Limits with Step-by-Step Solutions</title>
        <meta name="description" content="Calculate limits of functions at any point or at infinity. Includes one-sided limits, indeterminate forms, and detailed steps." />
      </Helmet>
      
      {/* Page Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg">
              <Target className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-foreground">Limit Calculator</h1>
              <p className="text-muted-foreground mt-1">Compute limits at any point or infinity</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExport} disabled={!result} className="gap-2">
              <Download className="w-4 h-4" />
              Export
            </Button>
            <Button variant="outline" size="sm" className="gap-2">
              <Share2 className="w-4 h-4" />
              Share
            </Button>
          </div>
        </div>
        
        {/* Quick Stats Bar */}
        {result && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-3"
          >
            <Card className="p-3 bg-purple-500/5 border-purple-500/20">
              <div className="text-xs text-muted-foreground mb-1">Type</div>
              <div className="text-lg font-bold text-purple-600 capitalize">{result.limitType}</div>
            </Card>
            <Card className="p-3 bg-pink-500/5 border-pink-500/20">
              <div className="text-xs text-muted-foreground mb-1">Direction</div>
              <div className="text-lg font-bold text-pink-600 capitalize">{result.direction}</div>
            </Card>
            <Card className="p-3 bg-fuchsia-500/5 border-fuchsia-500/20">
              <div className="text-xs text-muted-foreground mb-1">Steps</div>
              <div className="text-lg font-bold text-fuchsia-600">{result.steps.length}</div>
            </Card>
            <Card className="p-3 bg-violet-500/5 border-violet-500/20">
              <div className="text-xs text-muted-foreground mb-1">Status</div>
              <div className="text-sm font-bold text-violet-600">Computed</div>
            </Card>
          </motion.div>
        )}
      </motion.div>
      
      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 lg:grid-cols-4 h-auto">
          <TabsTrigger value="calculator" className="py-3">Calculator</TabsTrigger>
          <TabsTrigger value="laws" className="py-3">Limit Laws</TabsTrigger>
          <TabsTrigger value="examples" className="py-3">Examples</TabsTrigger>
          <TabsTrigger value="guide" className="py-3">Complete Guide</TabsTrigger>
        </TabsList>
        
        {/* Calculator Tab */}
        <TabsContent value="calculator" className="space-y-6">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Input Panel */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:sticky lg:top-24 lg:self-start space-y-6"
            >
              <Card className="rounded-2xl p-6 border-2 shadow-lg">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-purple-600" />
                    <span className="font-semibold text-foreground text-lg">Input Function</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-9 w-9 hover:bg-destructive/10 hover:text-destructive" 
                    onClick={handleReset}
                    title="Reset All"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-5">
                  <div>
                    <Label className="text-sm mb-2 block font-medium">Function f({variable})</Label>
                    <Input
                      type="text"
                      value={expression}
                      onChange={(e) => setExpression(e.target.value)}
                      className="h-12 text-lg font-mono"
                      placeholder="e.g., x^2 + 3x + 2"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Use ^ for powers (e.g., x^2, x^3)
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-sm mb-2 block font-medium">Variable</Label>
                    <Input
                      type="text"
                      value={variable}
                      onChange={(e) => setVariable(e.target.value)}
                      className="h-12 text-lg font-mono"
                      maxLength={1}
                    />
                  </div>
                  
                  <div>
                    <Label className="text-sm mb-2 block font-medium">{variable} approaches</Label>
                    <Input
                      type="text"
                      value={approachValue}
                      onChange={(e) => setApproachValue(e.target.value)}
                      className="h-12 text-lg font-mono"
                      placeholder="Number or 'inf' for infinity"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Enter a number, 'inf' for ∞, or '-inf' for -∞
                    </p>
                  </div>
                  
                  <div>
                    <Label className="text-sm mb-3 block font-medium">Direction</Label>
                    <Tabs value={direction} onValueChange={setDirection} className="w-full">
                      <TabsList className="w-full grid grid-cols-3 h-auto">
                        <TabsTrigger value="both" className="py-2.5 text-xs">
                          Both
                        </TabsTrigger>
                        <TabsTrigger value="left" className="py-2.5 text-xs">
                          Left (⁻)
                        </TabsTrigger>
                        <TabsTrigger value="right" className="py-2.5 text-xs">
                          Right (⁺)
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                    <p className="text-xs text-muted-foreground mt-2">
                      Two-sided limit or one-sided limit
                    </p>
                  </div>
                </div>
                
                {/* Validation Status */}
                <div className="mt-6">
                  {result ? (
                    <div className="flex items-center gap-2 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-green-600">Valid Expression ✓</span>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 p-3 bg-destructive/10 rounded-lg border border-destructive/20">
                      <XCircle className="w-5 h-5 text-destructive mt-0.5" />
                      <div>
                        <span className="text-sm font-medium text-destructive block">Invalid Expression</span>
                        <span className="text-xs text-destructive/80">Check your function syntax</span>
                      </div>
                    </div>
                  )}
                </div>
              </Card>
              
              {/* Quick Examples */}
              <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-purple-500/5 to-pink-500/5">
                <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-yellow-500" />
                  Quick Examples
                </h3>
                <div className="space-y-2">
                  {exampleProblems.map((ex, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      className="w-full justify-start text-left h-auto py-3"
                      onClick={() => loadExample(ex.expr, ex.value)}
                    >
                      <div className="flex-1">
                        <div className="font-mono text-sm">
                          lim ({variable}→{ex.value}) {ex.expr}
                        </div>
                        <div className="text-xs text-muted-foreground">{ex.desc}</div>
                      </div>
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  ))}
                </div>
              </Card>
            </motion.div>
            
            {/* Center/Right Column - Results */}
            <div className="lg:col-span-2 space-y-6">
              {/* Result Display */}
              {result && (
                <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-purple-500/5 to-fuchsia-500/5">
                  <h2 className="font-semibold text-foreground flex items-center gap-2 mb-5 text-lg">
                    <CheckCircle2 className="w-5 h-5 text-purple-600" />
                    Result
                  </h2>
                  
                  <div className="space-y-4">
                    <div className="p-6 bg-background/60 rounded-xl border-2">
                      <div className="text-sm text-muted-foreground mb-2">Computing limit:</div>
                      <div className="font-mono text-xl font-bold text-foreground mb-4">
                        lim ({result.variable}→{result.approachValue === Infinity ? '∞' : result.approachValue === -Infinity ? '-∞' : result.approachValue}
                        {result.direction === 'left' ? '⁻' : result.direction === 'right' ? '⁺' : ''}) {result.expression}
                      </div>
                      
                      <div className="text-sm text-muted-foreground mb-2">Limit value:</div>
                      <div className="font-mono text-3xl font-bold text-purple-600">
                        {result.result === Infinity ? '∞' : result.result === -Infinity ? '-∞' : typeof result.result === 'number' ? result.result.toFixed(6) : result.result}
                      </div>
                      
                      {result.isIndeterminate && (
                        <div className="mt-4 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                          <div className="flex items-start gap-2">
                            <Info className="w-4 h-4 text-amber-600 mt-0.5" />
                            <div>
                              <div className="text-sm font-semibold text-amber-600">Indeterminate Form</div>
                              <div className="text-xs text-amber-600/80 mt-1">
                                This limit may require L'Hôpital's Rule or algebraic manipulation to evaluate
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex gap-2">
                      <CopyButton text={result.result} label="limit" />
                      <Button variant="outline" size="sm" className="gap-2">
                        <Download className="w-3 h-3" />
                        Save Result
                      </Button>
                    </div>
                  </div>
                </Card>
              )}
              
              {/* Step-by-Step Solution */}
              {result && (
                <Card className="rounded-2xl p-6 border-2 shadow-lg">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="font-semibold text-foreground flex items-center gap-2 text-lg">
                      <BookOpen className="w-5 h-5 text-purple-600" />
                      Step-by-Step Solution
                      <Sparkles className="w-4 h-4 text-yellow-500 ml-auto" />
                    </h2>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSteps(!showSteps)}
                      className="gap-2"
                    >
                      {showSteps ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      <span className="hidden sm:inline">{showSteps ? 'Hide' : 'Show'}</span>
                    </Button>
                  </div>
                  
                  <AnimatePresence>
                    {showSteps && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="space-y-3"
                      >
                        {result.steps.map((step, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.03 }}
                            className="flex gap-3 p-3 bg-muted/30 rounded-lg border"
                          >
                            <div className="flex-shrink-0 w-7 h-7 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-bold">
                              {index + 1}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-foreground font-mono whitespace-pre-line">{step}</p>
                            </div>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              )}
              
              {/* Understanding Limits */}
              <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-blue-500/5 to-cyan-500/5">
                <h2 className="font-semibold text-foreground flex items-center gap-2 mb-5 text-lg">
                  <Info className="w-5 h-5 text-blue-600" />
                  Understanding Limits
                </h2>
                
                <div className="space-y-4 text-sm text-muted-foreground">
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">What is a Limit?</h3>
                    <p className="leading-relaxed">
                      A limit describes the value that a function approaches as the input approaches some value. 
                      It's the foundation of calculus and helps us understand function behavior at points where direct evaluation might not work.
                    </p>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Notation</h3>
                    <div className="bg-background/60 p-3 rounded-lg">
                      <code className="text-xs font-mono">lim (x→a) f(x) = L</code>
                      <p className="text-xs mt-2">Reads: "The limit of f(x) as x approaches a equals L"</p>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">One-Sided Limits</h3>
                    <ul className="space-y-2 list-disc list-inside">
                      <li><strong>Left-hand limit:</strong> lim (x→a⁻) f(x) - approaching from values less than a</li>
                      <li><strong>Right-hand limit:</strong> lim (x→a⁺) f(x) - approaching from values greater than a</li>
                      <li><strong>Two-sided limit exists</strong> only when left and right limits are equal</li>
                    </ul>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </TabsContent>
        
        {/* Limit Laws Tab */}
        <TabsContent value="laws" className="space-y-6">
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-purple-600" />
              Complete Limit Laws Reference
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              {limitLaws.map((law, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="p-5 rounded-xl border-2 bg-gradient-to-br from-purple-500/5 to-pink-500/5 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-bold text-lg text-foreground">{law.name}</h3>
                    <Badge variant="secondary" className="text-xs">{idx + 1}</Badge>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="bg-background/60 p-3 rounded-lg">
                      <div className="text-xs text-muted-foreground mb-1">Law:</div>
                      <code className="font-mono text-xs font-bold leading-relaxed">{law.formula}</code>
                    </div>
                    
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {law.description}
                    </p>
                    
                    <div className="text-xs text-amber-600 bg-amber-500/10 p-2 rounded">
                      <strong>Condition:</strong> {law.condition}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </Card>
          
          {/* Indeterminate Forms */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-orange-500/5 to-red-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-orange-600" />
              Indeterminate Forms & Techniques
            </h2>
            
            <div className="space-y-4">
              <div className="p-5 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-3 text-orange-600">Seven Indeterminate Forms</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {['0/0', '∞/∞', '0·∞', '∞ - ∞', '0^0', '1^∞', '∞^0'].map((form, idx) => (
                    <div key={idx} className="p-3 bg-background rounded text-center font-mono font-bold text-lg border">
                      {form}
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground mt-3">
                  These forms require special techniques like L'Hôpital's Rule, algebraic manipulation, or Taylor series.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">L'Hôpital's Rule</h3>
                <div className="text-sm space-y-2">
                  <p className="text-muted-foreground">For 0/0 or ∞/∞ forms:</p>
                  <code className="font-mono text-sm block p-3 bg-background rounded">
                    lim f(x)/g(x) = lim f'(x)/g'(x)
                  </code>
                  <p className="text-xs text-muted-foreground">
                    Keep applying until you get a determinate form. Can be applied repeatedly if needed.
                  </p>
                </div>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">Algebraic Simplification</h3>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p>Techniques to resolve 0/0 forms:</p>
                  <ul className="list-disc list-inside ml-2 space-y-1">
                    <li>Factor numerator and denominator, cancel common factors</li>
                    <li>Rationalize by multiplying by conjugate</li>
                    <li>Combine fractions over common denominator</li>
                    <li>Use trigonometric identities</li>
                  </ul>
                </div>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">Special Limit Techniques</h3>
                <div className="space-y-2 text-sm">
                  <div className="p-2 bg-background rounded">
                    <strong>Squeeze Theorem:</strong>
                    <p className="text-xs text-muted-foreground mt-1">
                      Trap function between two others with known limits
                    </p>
                  </div>
                  <div className="p-2 bg-background rounded">
                    <strong>Dominant Term:</strong>
                    <p className="text-xs text-muted-foreground mt-1">
                      For limits at infinity, identify highest power term
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>
        
        {/* Examples Tab */}
        <TabsContent value="examples" className="space-y-6">
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-yellow-500" />
              Worked Examples
            </h2>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="ex-1">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 1: Direct Substitution</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Problem:</strong> Find lim (x→2) (x² + 3x + 2)
                    </div>
                    <div>
                      <strong>Solution:</strong>
                      <ol className="list-decimal list-inside space-y-2 mt-2 ml-2">
                        <li>Check if function is continuous at x = 2</li>
                        <li>Since it's a polynomial (continuous everywhere), use direct substitution</li>
                        <li>Substitute x = 2: (2)² + 3(2) + 2 = 4 + 6 + 2 = 12</li>
                      </ol>
                    </div>
                    <div className="bg-green-500/10 p-3 rounded border border-green-500/20">
                      <strong className="text-green-600">Answer:</strong> <code className="font-mono ml-2">12</code>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="ex-2">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 2: Factoring (0/0 Form)</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Problem:</strong> Find lim (x→2) (x² - 4)/(x - 2)
                    </div>
                    <div>
                      <strong>Solution:</strong>
                      <ol className="list-decimal list-inside space-y-2 mt-2 ml-2">
                        <li>Direct substitution gives 0/0 (indeterminate)</li>
                        <li>Factor numerator: x² - 4 = (x + 2)(x - 2)</li>
                        <li>Cancel (x - 2): [(x + 2)(x - 2)]/(x - 2) = x + 2</li>
                        <li>Now substitute x = 2: 2 + 2 = 4</li>
                      </ol>
                    </div>
                    <div className="bg-green-500/10 p-3 rounded border border-green-500/20">
                      <strong className="text-green-600">Answer:</strong> <code className="font-mono ml-2">4</code>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="ex-3">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 3: Limit at Infinity</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Problem:</strong> Find lim (x→∞) (3x² + 2x)/(5x² - x + 1)
                    </div>
                    <div>
                      <strong>Solution:</strong>
                      <ol className="list-decimal list-inside space-y-2 mt-2 ml-2">
                        <li>Identify highest power in numerator and denominator: x²</li>
                        <li>Divide every term by x²:</li>
                        <li>= lim (x→∞) (3 + 2/x)/(5 - 1/x + 1/x²)</li>
                        <li>As x→∞, terms with x in denominator approach 0</li>
                        <li>= 3/5</li>
                      </ol>
                    </div>
                    <div className="bg-green-500/10 p-3 rounded border border-green-500/20">
                      <strong className="text-green-600">Answer:</strong> <code className="font-mono ml-2">3/5 = 0.6</code>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="ex-4">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 4: One-Sided Limit</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Problem:</strong> Find lim (x→0⁺) 1/x and lim (x→0⁻) 1/x
                    </div>
                    <div>
                      <strong>Solution:</strong>
                      <ol className="list-decimal list-inside space-y-2 mt-2 ml-2">
                        <li>{"From the right (x→0⁺): x > 0, so 1/x > 0 and grows without bound"}</li>
                        <li>Right-hand limit: +∞</li>
                        <li>{"From the left (x→0⁻): x < 0, so 1/x < 0 and becomes more negative"}</li>
                        <li>Left-hand limit: -∞</li>
                        <li>Since left ≠ right, two-sided limit does not exist</li>
                      </ol>
                    </div>
                    <div className="bg-amber-500/10 p-3 rounded border border-amber-500/20">
                      <strong className="text-amber-600">Answer:</strong> <span className="ml-2">Right: +∞, Left: -∞, Two-sided: DNE</span>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="ex-5">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">Example 5: Rationalization</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                    <div>
                      <strong>Problem:</strong> Find lim (x→0) (√(x+1) - 1)/x
                    </div>
                    <div>
                      <strong>Solution:</strong>
                      <ol className="list-decimal list-inside space-y-2 mt-2 ml-2">
                        <li>Direct substitution gives 0/0</li>
                        <li>Multiply by conjugate: [(√(x+1) - 1)/x] · [(√(x+1) + 1)/(√(x+1) + 1)]</li>
                        <li>= [(x+1) - 1]/[x(√(x+1) + 1)] = x/[x(√(x+1) + 1)]</li>
                        <li>Cancel x: 1/(√(x+1) + 1)</li>
                        <li>Substitute x = 0: 1/(√1 + 1) = 1/2</li>
                      </ol>
                    </div>
                    <div className="bg-green-500/10 p-3 rounded border border-green-500/20">
                      <strong className="text-green-600">Answer:</strong> <code className="font-mono ml-2">1/2 = 0.5</code>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>
          
          {/* Practice Problems */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-orange-500/5 to-red-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Hash className="w-5 h-5 text-orange-600" />
              Practice Problems
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                "lim (x→3) (x² - 9)/(x - 3)",
                "lim (x→∞) (2x + 1)/(x - 5)",
                "lim (x→0) (sin x)/x",
                "lim (x→1) (x³ - 1)/(x - 1)",
                "lim (x→-∞) (3x² + x)/(x² - 1)",
                "lim (x→0) (e^x - 1)/x",
                "lim (x→4) √(x+5) - 3)/(x - 4)",
                "lim (x→∞) x²/(e^x)"
              ].map((problem, idx) => (
                <div key={idx} className="p-4 bg-background/60 rounded-lg border hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-orange-600 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                      {idx + 1}
                    </div>
                    <div className="font-mono text-sm">{problem}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
        
        {/* Complete Guide Tab */}
        <TabsContent value="guide" className="space-y-6">
          {/* FAQ Section */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg">
            <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-yellow-500" />
              Frequently Asked Questions
            </h2>
            
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="faq-1">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">What's the difference between limit and value?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-muted-foreground leading-relaxed">
                      The limit describes what a function approaches, while the value is what it actually equals at that point. 
                      These can be different!
                    </p>
                    <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/20">
                      <strong className="text-blue-600">Example:</strong>
                      <p className="text-sm mt-2">Consider f(x) = (x²-4)/(x-2)</p>
                      <ul className="list-disc list-inside space-y-1 mt-2 text-sm">
                        <li>At x=2: f(2) = 0/0 = undefined (value doesn't exist)</li>
                        <li>But: lim (x→2) f(x) = 4 (limit exists)</li>
                      </ul>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Limits help us understand function behavior even where the function isn't defined.
                    </p>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-2">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">When do limits not exist?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-muted-foreground leading-relaxed">
                      A limit fails to exist in several scenarios:
                    </p>
                    <div className="space-y-3">
                      <div className="bg-background/60 p-3 rounded-lg border">
                        <strong className="text-sm">1. Different One-Sided Limits</strong>
                        <p className="text-sm text-muted-foreground mt-1">
                          If lim (x→a⁻) f(x) ≠ lim (x→a⁺) f(x), the two-sided limit DNE.
                          Example: f(x) = |x|/x at x=0
                        </p>
                      </div>
                      <div className="bg-background/60 p-3 rounded-lg border">
                        <strong className="text-sm">2. Infinite Oscillation</strong>
                        <p className="text-sm text-muted-foreground mt-1">
                          Function oscillates infinitely fast. Example: sin(1/x) as x→0
                        </p>
                      </div>
                      <div className="bg-background/60 p-3 rounded-lg border">
                        <strong className="text-sm">3. Unbounded Behavior</strong>
                        <p className="text-sm text-muted-foreground mt-1">
                          Function goes to ∞ or -∞ (we sometimes say "limit is infinite")
                        </p>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-3">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">How do I evaluate limits at infinity?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-muted-foreground leading-relaxed">
                      For rational functions (polynomials divided by polynomials):
                    </p>
                    <div className="space-y-2">
                      <div className="p-3 bg-background/60 rounded-lg border">
                        <strong className="text-sm">Method: Compare Highest Powers</strong>
                        <ol className="list-decimal list-inside space-y-1 mt-2 text-sm text-muted-foreground">
                          <li>Identify degree of numerator (n) and denominator (m)</li>
                          <li>If n &lt; m: limit = 0</li>

                          <li>If n = m: limit = ratio of leading coefficients</li>
<li>If n &gt; m: limit = ±∞ (depends on signs and even/odd)</li>

                        </ol>
                      </div>
                      <div className="p-3 bg-blue-500/10 rounded border border-blue-500/20">
                        <strong className="text-sm text-blue-600">Example:</strong>
                        <p className="text-xs mt-2 font-mono">
                          lim (x→∞) (3x² + 2)/(5x² - x) = 3/5 (same degree)
                        </p>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-4">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">What are the most important limit theorems?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <div className="space-y-3">
                      <div className="p-4 bg-background/60 rounded-lg border">
                        <strong>Squeeze Theorem</strong>
                        <p className="text-sm text-muted-foreground mt-2">
                          If f(x) ≤ g(x) ≤ h(x) near a, and lim f(x) = lim h(x) = L, then lim g(x) = L.
                          Essential for proving lim (x→0) (sin x)/x = 1.
                        </p>
                      </div>
                      <div className="p-4 bg-background/60 rounded-lg border">
                        <strong>Intermediate Value Theorem</strong>
                        <p className="text-sm text-muted-foreground mt-2">
                          If f is continuous on [a,b] and k is between f(a) and f(b), then there exists c in (a,b) where f(c) = k.
                          Guarantees roots exist.
                        </p>
                      </div>
                      <div className="p-4 bg-background/60 rounded-lg border">
                        <strong>Continuity Theorem</strong>
                        <p className="text-sm text-muted-foreground mt-2">
                          If f is continuous at a, then lim (x→a) f(x) = f(a). This is why direct substitution works for continuous functions.
                        </p>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="faq-5">
                <AccordionTrigger className="text-left hover:no-underline py-4">
                  <span className="font-medium">What's the relationship between limits, continuity, and derivatives?</span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-3">
                    <p className="text-muted-foreground leading-relaxed">
                      These three concepts form the foundation of calculus and are deeply connected:
                    </p>
                    <div className="space-y-3">
                      <div className="p-4 bg-background/60 rounded-lg border">
                        <strong>Limits → Continuity</strong>
                        <p className="text-sm text-muted-foreground mt-2">
                          A function f is continuous at a if: lim (x→a) f(x) = f(a). Limits define continuity.
                        </p>
                      </div>
                      <div className="p-4 bg-background/60 rounded-lg border">
                        <strong>Limits → Derivatives</strong>
                        <p className="text-sm text-muted-foreground mt-2">
                          The derivative is defined as a limit: f'(a) = lim (h→0) [f(a+h) - f(a)]/h
                        </p>
                      </div>
                      <div className="p-4 bg-background/60 rounded-lg border">
                        <strong>Derivatives → Continuity</strong>
                        <p className="text-sm text-muted-foreground mt-2">
                          If f is differentiable at a, then f is continuous at a (but not vice versa).
                        </p>
                      </div>
                    </div>
                    <div className="bg-purple-500/10 p-3 rounded border border-purple-500/20 mt-3">
                      <strong className="text-sm text-purple-600">Chain:</strong>
                      <p className="text-xs text-muted-foreground mt-1">
                        Differentiable ⇒ Continuous ⇒ Limit exists (but arrows don't reverse)
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Card>
          
          {/* Real-World Applications */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-purple-500/5 to-pink-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-600" />
              Real-World Applications of Limits
            </h2>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  Physics - Instantaneous Rates
                </h3>
                <p className="text-xs text-muted-foreground">
                  Velocity is the limit of average velocity as time interval approaches zero. 
                  Acceleration is similarly defined using limits.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <Calculator className="w-4 h-4 text-blue-600" />
                  Engineering - Stability Analysis
                </h3>
                <p className="text-xs text-muted-foreground">
                  Determining if systems remain bounded as time approaches infinity, 
                  critical for control systems and circuit design.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <InfinityIcon className="w-4 h-4 text-purple-600" />
                  Computer Science - Complexity
                </h3>
                <p className="text-xs text-muted-foreground">
                  Big-O notation uses limits to describe algorithm behavior as input size approaches infinity, 
                  essential for performance analysis.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <Info className="w-4 h-4 text-orange-600" />
                  Economics - Marginal Analysis
                </h3>
                <p className="text-xs text-muted-foreground">
                  Marginal cost, revenue, and profit are all limits describing rates of change 
                  as production approaches a certain level.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <Sparkles className="w-4 h-4 text-cyan-600" />
                  Medicine - Drug Concentration
                </h3>
                <p className="text-xs text-muted-foreground">
                  Understanding drug concentration limits as time approaches infinity determines 
                  steady-state dosing and safety margins.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2 flex items-center gap-2 text-sm">
                  <Target className="w-4 h-4 text-yellow-600" />
                  Finance - Continuous Compounding
                </h3>
                <p className="text-xs text-muted-foreground">
                  The formula e^(rt) comes from taking the limit as compounding frequency 
                  approaches infinity.
                </p>
              </div>
            </div>
          </Card>
          
          {/* Historical Context */}
          <Card className="rounded-2xl p-6 border-2 shadow-lg bg-gradient-to-br from-blue-500/5 to-cyan-500/5">
            <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-blue-600" />
              Historical Development of Limits
            </h2>
            
            <div className="space-y-4">
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">Ancient Greeks - Method of Exhaustion</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Archimedes used limit-like reasoning to calculate areas and volumes by approximating with increasingly fine polygons, 
                  though he didn't formalize the concept of limits.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">17th Century - Infinitesimals</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Newton and Leibniz developed calculus using informal notions of "infinitely small" quantities. 
                  While powerful, these lacked rigorous foundations and led to philosophical debates.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">19th Century - Rigorous Foundations</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Cauchy, Weierstrass, and others developed the precise ε-δ definition of limits, 
                  placing calculus on solid logical ground and resolving centuries of controversy.
                </p>
              </div>
              
              <div className="p-4 bg-background/60 rounded-lg border">
                <h3 className="font-semibold mb-2">20th Century - Modern Analysis</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Limits extended to abstract spaces (metric spaces, topological spaces), 
                  becoming fundamental to modern mathematics, physics, and computer science.
                </p>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default LimitCalculator;